import { Copy, DownloadSimple, ShareNetwork } from '@phosphor-icons/react';
import type {
  SharedPostAccountDto,
  SharedPostDto,
  SharedPostMediaDto,
} from '@reelplicate/api-client/model';
import { useGetSharedPost } from '@reelplicate/api-client/post-shares';
import {
  Button,
  Inline,
  ProgressBar,
  Skeleton,
  Stack,
  toast,
  Well,
} from '@reelplicate/ui';
import { useId } from 'react';
import { useParams } from 'react-router';

import { AuthCard } from '@/features/auth/auth-card';
import { formatWhen } from '@/lib/format-when';

import { type MediaFiles, useMediaFiles } from './use-media-files';

const PLATFORM_NAMES: Record<SharedPostAccountDto['platform'], string> = {
  TIKTOK: 'TikTok',
  INSTAGRAM: 'Instagram',
  YOUTUBE: 'YouTube',
  LINKEDIN: 'LinkedIn',
  FACEBOOK: 'Facebook',
};

/** The card's padding, a little tighter on a phone. */
const CARD = 'px-5 sm:px-8';

/**
 * "Post from phone" (`/share/:token`), opened from the QR code in a Calendar
 * post: the post's files to save and its caption to copy, so it can be posted
 * by hand in TikTok or Instagram. No sign-in - the link is the permission.
 */
export function SharePage() {
  const { token = '' } = useParams();
  const shared = useGetSharedPost(token, {
    query: { enabled: Boolean(token), retry: false, throwOnError: false },
  });

  if (shared.isPending && token) {
    return (
      <AuthCard className={CARD}>
        <Stack gap={4} aria-busy="true" aria-label="Loading the post">
          <Skeleton className="h-7 w-56" />
          <Skeleton shape="rectangle" shimmer className="h-64 w-full" />
          <Skeleton shape="rectangle" shimmer className="h-24 w-full" />
        </Stack>
      </AuthCard>
    );
  }
  if (!shared.data) {
    return (
      <AuthCard className={CARD}>
        <Well tone="danger" title="This link is no longer valid">
          It may have expired or been revoked. Make a new one from the post in
          Reelplicate.
        </Well>
      </AuthCard>
    );
  }
  return <SharedPost post={shared.data} />;
}

function SharedPost({ post }: { post: SharedPostDto }) {
  const media = useMediaFiles(post.media);
  const captions = distinctCaptions(post.accounts);
  const titles = distinctTitles(post.accounts);
  return (
    <AuthCard className={CARD}>
      <Stack gap={1}>
        <h1 className="text-heading-h3 text-text-primary">{post.title}</h1>
        <p className="text-body-sm text-text-secondary">
          {post.accounts
            .map((a) => `${PLATFORM_NAMES[a.platform]} ${a.handle}`)
            .join(' · ')}{' '}
          · {formatWhen(post.scheduledAt)}
        </p>
      </Stack>

      <Previews media={post.media} />
      <SaveFiles
        media={post.media}
        files={media}
        carousel={post.dataType === 'carousel'}
      />

      {titles.map((title) => (
        <CopyBlock key={title.label} what="Title" {...title} />
      ))}
      {captions.map((caption) => (
        <CopyBlock key={caption.label} what="Caption" {...caption} />
      ))}
    </AuthCard>
  );
}

/** One caption block per different caption; one plain "Caption" when they match. */
function distinctCaptions(accounts: SharedPostAccountDto[]) {
  const texts = [...new Set(accounts.map((a) => a.caption ?? ''))].filter(Boolean);
  if (texts.length <= 1) return texts.map((text) => ({ label: 'Caption', text }));
  return accounts
    .filter((a) => a.caption)
    .map((a) => ({
      label: `Caption for ${PLATFORM_NAMES[a.platform]} ${a.handle}`,
      text: a.caption!,
    }));
}

/** TikTok's and YouTube's title field: one block per different title. */
function distinctTitles(accounts: SharedPostAccountDto[]) {
  const titled = accounts.filter((a) => a.postTitle);
  const texts = [...new Set(titled.map((a) => a.postTitle!))];
  if (texts.length === 1) {
    const where = [...new Set(titled.map((a) => PLATFORM_NAMES[a.platform]))].join(
      ' and ',
    );
    return [{ label: `Title for ${where}`, text: texts[0]! }];
  }
  return titled.map((a) => ({
    label: `Title for ${PLATFORM_NAMES[a.platform]} ${a.handle}`,
    text: a.postTitle!,
  }));
}

function Previews({ media }: { media: SharedPostMediaDto[] }) {
  if (media.length === 0) return null;
  return (
    <ul
      aria-label="Files"
      className="-mx-5 flex snap-x gap-2 overflow-x-auto px-5 sm:-mx-8 sm:px-8"
    >
      {media.map((item) => (
        <li key={item.url} className="w-48 shrink-0 snap-start">
          {item.kind === 'video' ? (
            <video
              src={item.url}
              controls
              playsInline
              preload="metadata"
              aria-label={item.fileName}
              className="bg-bg-subtle aspect-[9/16] w-full rounded-lg object-cover"
            />
          ) : (
            <img
              src={item.url}
              alt={item.fileName}
              className="bg-bg-subtle aspect-[4/5] w-full rounded-lg object-cover"
            />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Save to Photos through the share sheet (once every file is here), with a
 * download link per file as the fallback - iOS quietly limits what a share
 * can carry.
 */
function SaveFiles({
  media,
  files,
  carousel,
}: {
  media: SharedPostMediaDto[];
  files: MediaFiles;
  carousel: boolean;
}) {
  if (media.length === 0) return null;
  const canShare =
    files.files !== null &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: files.files });
  const label = carousel
    ? `Save ${media.length} ${media.length === 1 ? 'image' : 'images'}`
    : 'Save the video';

  const share = () => {
    // Nothing may be awaited before this, or iOS drops the tap.
    navigator.share({ files: files.files! }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      toast.error('Couldn’t open sharing', {
        description: 'Use the download links instead.',
      });
    });
  };

  return (
    <Stack gap={3}>
      {files.files === null && !files.failed && (
        <ProgressBar
          size="sm"
          aria-label="Getting the files ready"
          value={files.total ? Math.min(files.loaded, files.total) : null}
          max={files.total || undefined}
        />
      )}
      {canShare && (
        <Stack gap={2}>
          <Button size="lg" leadingIcon={ShareNetwork} className="w-full" onClick={share}>
            {label}
          </Button>
          <p className="text-caption text-text-secondary">
            {carousel
              ? 'In the share sheet tap Save Images, then build the post in TikTok from your photos.'
              : 'In the share sheet tap Save Video, then post it from TikTok.'}
          </p>
        </Stack>
      )}
      {files.files !== null && !canShare && (
        <p className="text-caption text-text-secondary">
          Sharing isn’t available in this browser - download the files below.
        </p>
      )}
      <Stack as="ul" gap={1} aria-label="Downloads">
        {media.map((item) => (
          <li key={item.url}>
            <a
              href={item.url}
              download={item.fileName}
              className="text-body-sm text-text-primary hover:bg-bg-subtle focus-visible:shadow-focus-ring flex items-center gap-2 rounded-md px-2 py-1.5 outline-none"
            >
              <DownloadSimple aria-hidden className="text-icon-secondary size-4" />
              <span className="min-w-0 flex-1 truncate">{item.fileName}</span>
              {item.size != null && (
                <span className="text-caption text-text-tertiary">
                  {formatSize(item.size)}
                </span>
              )}
            </a>
          </li>
        ))}
      </Stack>
    </Stack>
  );
}

function CopyBlock({
  what,
  label,
  text,
}: {
  what: 'Title' | 'Caption';
  label: string;
  text: string;
}) {
  const id = useId();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copied`);
    } catch {
      // No clipboard (plain http, old browsers): select it to copy by hand.
      const node = document.getElementById(id);
      if (node) window.getSelection()?.selectAllChildren(node);
      toast.info(`${what} selected - copy it from the menu`);
    }
  };
  return (
    <Stack gap={2}>
      <Inline gap={2} className="justify-between">
        <h2 className="text-label-md text-text-primary">{label}</h2>
        <Button
          size="sm"
          variant="outline"
          leadingIcon={Copy}
          onClick={() => void copy()}
        >
          Copy {what.toLowerCase()}
        </Button>
      </Inline>
      <p
        id={id}
        className="bg-bg-subtle text-body-md text-text-primary rounded-md px-3 py-2.5 whitespace-pre-wrap select-all"
      >
        {text}
      </p>
    </Stack>
  );
}

/** "2.4 MB", "820 KB". */
function formatSize(bytes: number) {
  return bytes >= 1_000_000
    ? `${(bytes / 1_000_000).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1000))} KB`;
}
